/**
 * NestJS Common Shim for Browser Environments
 *
 * This file provides no-op implementations of NestJS common decorators and utilities
 * for use in web applications. When bundling for the browser, Vite can be configured
 * to alias '@nestjs/common' to this file, preventing NestJS from being bundled into
 * the browser application.
 *
 * The functions do nothing in the browser - they just return no-op decorators.
 * This allows shared code that uses NestJS decorators to be imported without
 * triggering NestJS's server-side logic.
 */

/**
 * No-op implementation of applyDecorators
 * In NestJS, this combines multiple decorators into one.
 * In the browser, we just return a no-op decorator.
 */
export function applyDecorators(
	..._decorators: Array<unknown>
): PropertyDecorator & MethodDecorator & ClassDecorator {
	return () => {
		// No-op
	};
}

/**
 * No-op implementation of SetMetadata
 * In NestJS, this sets metadata on a route handler.
 * In the browser, we just return a no-op decorator.
 */
export function SetMetadata(
	_key: string,
	_value: unknown,
): PropertyDecorator & MethodDecorator & ClassDecorator {
	return () => {
		// No-op
	};
}

/**
 * No-op implementations of other commonly used NestJS exports
 * Add more as needed based on what gets imported
 */

export function Injectable(): ClassDecorator {
	return (target) => {
		return target;
	};
}

export function Inject(_token?: unknown): ParameterDecorator & PropertyDecorator {
	return () => {
		// No-op
	};
}

export function Optional(): ParameterDecorator & PropertyDecorator {
	return () => {
		// No-op
	};
}

export function Controller(_prefix?: string | Array<string>): ClassDecorator {
	return (target) => {
		return target;
	};
}

export function Get(_path?: string | Array<string>): MethodDecorator {
	return () => {
		// No-op
	};
}

export function Post(_path?: string | Array<string>): MethodDecorator {
	return () => {
		// No-op
	};
}

export function Put(_path?: string | Array<string>): MethodDecorator {
	return () => {
		// No-op
	};
}

export function Delete(_path?: string | Array<string>): MethodDecorator {
	return () => {
		// No-op
	};
}

export function Patch(_path?: string | Array<string>): MethodDecorator {
	return () => {
		// No-op
	};
}

export function Body(_property?: string): ParameterDecorator {
	return () => {
		// No-op
	};
}

export function Query(_property?: string): ParameterDecorator {
	return () => {
		// No-op
	};
}

export function Param(_property?: string): ParameterDecorator {
	return () => {
		// No-op
	};
}

export function Headers(_property?: string): ParameterDecorator {
	return () => {
		// No-op
	};
}

export function UseGuards(..._guards: Array<unknown>): MethodDecorator & ClassDecorator {
	return () => {
		// No-op
	};
}

export function UseInterceptors(..._interceptors: Array<unknown>): MethodDecorator & ClassDecorator {
	return () => {
		// No-op
	};
}

export function UsePipes(..._pipes: Array<unknown>): MethodDecorator & ClassDecorator {
	return () => {
		// No-op
	};
}

export class HttpException extends Error {
	constructor(
		response: string | Record<string, unknown>,
		public readonly status: number,
	) {
		super(typeof response === 'string' ? response : JSON.stringify(response));
	}

	getStatus(): number {
		return this.status;
	}

	getResponse(): string | Record<string, unknown> {
		return this.message;
	}
}

export class BadRequestException extends HttpException {
	constructor(message?: string) {
		super(message ?? 'Bad Request', 400);
	}
}

export class UnauthorizedException extends HttpException {
	constructor(message?: string) {
		super(message ?? 'Unauthorized', 401);
	}
}

export class ForbiddenException extends HttpException {
	constructor(message?: string) {
		super(message ?? 'Forbidden', 403);
	}
}

export class NotFoundException extends HttpException {
	constructor(message?: string) {
		super(message ?? 'Not Found', 404);
	}
}

export class InternalServerErrorException extends HttpException {
	constructor(message?: string) {
		super(message ?? 'Internal Server Error', 500);
	}
}

export enum HttpStatus {
	CONTINUE = 100,
	SWITCHING_PROTOCOLS = 101,
	PROCESSING = 102,
	OK = 200,
	CREATED = 201,
	ACCEPTED = 202,
	NO_CONTENT = 204,
	MOVED_PERMANENTLY = 301,
	FOUND = 302,
	BAD_REQUEST = 400,
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	NOT_FOUND = 404,
	METHOD_NOT_ALLOWED = 405,
	CONFLICT = 409,
	GONE = 410,
	UNPROCESSABLE_ENTITY = 422,
	INTERNAL_SERVER_ERROR = 500,
	NOT_IMPLEMENTED = 501,
	BAD_GATEWAY = 502,
	SERVICE_UNAVAILABLE = 503,
	GATEWAY_TIMEOUT = 504,
}